import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  FormControlLabel,
  Switch,
  TextField
} from '@mui/material';
import { Description, Refresh, Settings, Save } from '@mui/icons-material';


interface Facility {
  id: number;
  name: string;
}

interface BolDashboardItem {
  shipmentId: string;
  referenceNumber: string;
  shipmentStatus: string;
  direction: string;
  bolId: string;
  docNumber: string;
  estimatedBolDate: string;
  carrierName: string;
  driverId: string;
  driverName: string;
  driverPhoneNumber: string;
  driverFrId: string;
  shipFrom: string;
  shipTo: string;
  checkIn: string;
  checkOut: string;
  stopCount: number;
  pgi: string;
  uploadedBol: string;
  signedArtifact: string;
  shipmentLastUpdated: string;
  minutesSinceUpdate: number;
  isOverdue: boolean;
}

interface BolDashboardResponse {
  items: BolDashboardItem[];
  cachedAt: string;
  facilityId: string;
  totalCount: number;
  overdueCount: number;
}

interface BolDashboardWidgetProps {
  selectedFacility: Facility | null;
  compactMode: boolean;
  data?: Record<string, any> | null;
  isRefreshing?: boolean;
  onRefresh?: () => void;
  onBolSinceHoursChange?: (hours: number) => void;
}

const BolDashboardWidget: React.FC<BolDashboardWidgetProps> = ({
  selectedFacility,
  compactMode,
  data: dashboardData,
  isRefreshing = false,
  onRefresh,
  onBolSinceHoursChange
}) => {
  const [error, setError] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [saving, setSaving] = useState(false);

  // Widget configuration with localStorage persistence
  const [config, setConfig] = useState(() => {
    const saved = localStorage.getItem('bolWidgetConfig');
    return saved ? JSON.parse(saved) : {
      sinceHours: 2,
      showOnlyOverdue: false
    };
  });

  // Save config to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('bolWidgetConfig', JSON.stringify(config));
  }, [config]);

  // Notify parent when sinceHours changes so the consolidated query is rebuilt
  useEffect(() => {
    onBolSinceHoursChange?.(config.sinceHours);
  }, [config.sinceHours]);

  // Process raw data from consolidated query
  const data: BolDashboardResponse | null = useMemo(() => {
    return dashboardData?.bolDashboard || null;
  }, [dashboardData?.bolDashboard]);

  const loading = !dashboardData && !error;

  // Save widget configuration
  const handleSaveConfig = async () => {
    if (!selectedFacility) return;
    try {
      setSaving(true);
      console.log('BOL widget configuration saved successfully');
    } catch (err) {
      console.error('Failed to save widget configuration:', err);
    } finally {
      setSaving(false);
    }
  };

  const sortedItems = useMemo(() => {
    if (!data?.items) return [];
    let items = [...data.items];
    if (config.showOnlyOverdue) {
      items = items.filter(item => item.isOverdue);
    }
    return items.sort((a, b) => b.minutesSinceUpdate - a.minutesSinceUpdate);
  }, [data, config.showOnlyOverdue]);

  const formatMinutes = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const formatDirection = (direction: string): string => {
    if (!direction) return '';
    return direction.toLowerCase() === 'inbound' ? 'IB' : 'OB';
  };

  if (!selectedFacility) {
    return (
      <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Select a facility</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: showConfig ? 'visible' : 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Description sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            BOL Awaiting Signature
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {!loading && data && (
            <>
              {data.overdueCount > 0 && (
                <Chip
                  label={`${data.overdueCount} overdue`}
                  size="small"
                  color="error"
                  variant="filled"
                />
              )}
              <Typography variant="body2" color="text.secondary">
                {data.totalCount} total
              </Typography>
            </>
          )}
          <Tooltip title="Save Configuration">
            <IconButton
              onClick={handleSaveConfig}
              disabled={saving || !selectedFacility}
              size="small"
              color="primary"
            >
              <Save />
            </IconButton>
          </Tooltip>
          <Tooltip title="Configure">
            <IconButton
              onClick={() => setShowConfig(!showConfig)}
              size="small"
              color={showConfig ? 'primary' : 'default'}
            >
              <Settings />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} disabled={loading && !isRefreshing} size="small">
              <Refresh className={isRefreshing ? 'animate-spin' : ''} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Configuration Panel */}
      <Collapse in={showConfig}>
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Widget Configuration</Typography>

          <TextField
            label="Lookback window (hours)"
            type="number"
            size="small"
            value={config.sinceHours}
            onChange={(e) => setConfig((prev: any) => ({ ...prev, sinceHours: parseInt(e.target.value) || 2 }))}
            sx={{ width: 180, mb: 1 }}
            inputProps={{ min: 1, max: 48 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={config.showOnlyOverdue}
                onChange={(e) => setConfig((prev: any) => ({ ...prev, showOnlyOverdue: e.target.checked }))}
                size="small"
              />
            }
            label="Show only overdue BOLs"
            sx={{ display: 'block' }}
          />
        </Box>
      </Collapse>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Box>
      ) : sortedItems.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <Typography color="text.secondary" variant="body2">{`No unsigned BOLs in the last ${config.sinceHours} ${config.sinceHours === 1 ? 'hour' : 'hours'}`}</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Ref #</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Dir</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Carrier</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', py: 0.5 }}>Age</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedItems.map((item) => (
                <TableRow
                  key={`${item.shipmentId}-${item.bolId}`}
                  sx={{
                    bgcolor: item.isOverdue ? 'error.light' : 'inherit',
                    '& td': {
                      color: item.isOverdue ? 'error.contrastText' : 'inherit',
                    },
                    '&:last-child td': { border: 0 },
                    transition: 'opacity 0.3s ease, background-color 0.3s ease'
                  }}
                >
                  <TableCell sx={{ py: 0.5 }}>{item.referenceNumber}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{formatDirection(item.direction)}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{item.carrierName || '-'}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{item.shipmentStatus || '-'}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontWeight: item.isOverdue ? 'bold' : 'normal' }}>
                    {formatMinutes(item.minutesSinceUpdate)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default BolDashboardWidget;
